import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { Component, computed, input, InputSignal, OnInit, output, OutputEmitterRef, Signal } from '@angular/core';
import { ControlContainer, FormArray, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { Mapper, MapperPipe } from 'tiime-expert-utils';
import {
  TiimeButtonModule,
  TiimeButtonToggleModule,
  TiimeDatepickerModule,
  TiimeFormModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeTooltipModule
} from 'tiime-material';

import { MissionAcceptanceFormRowComponent } from '@common-shared/components/forms/mission-acceptance-form-row/mission-acceptance-form-row.component';
import { MissionAcceptanceFileType } from '@enums/mission-acceptance-file-type.enum';
import { MissionAcceptanceType } from '@enums/mission-acceptance-type.enum';
import { MissionAcceptanceFileForm } from '@forms/mission-acceptance-file-form';
import { MissionAcceptanceInformationsForm } from '@forms/mission-acceptance-form/mission-acceptance-informations-form';
import { CodeAndLabel } from '@models/code-and-label';
import { MissionAcceptanceFile } from '@models/mission-acceptance-file';
import { User } from '@models/user';
import { MissionAcceptanceFilesFormTableComponent } from '@shared-dialogs/create-or-update-mission-acceptance-dialog/mission-acceptance-files-form-table/mission-acceptance-files-form-table.component';
import { DigitOnlyDirective } from '@shared-directives/digit-only/digit-only.directive';
import { UPLOAD_FILE_CONFIG } from '@shared-overlays/upload-file-overlay/upload-file-config.constant';
import { CodeAndLabelSelectComponent } from '@shared-selectors/code-and-label-select/code-and-label-select.component';

export type MissionAcceptanceFileSelectedOutput = { fileList: FileList; type: MissionAcceptanceFileType };
type AddFileContext = { type: MissionAcceptanceFileType; text: string };

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-informations-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TiimeButtonModule,
    TiimeButtonToggleModule,
    TiimeInputContainerModule,
    TiimeFormModule,
    TiimeDatepickerModule,
    TiimeIconModule,
    TiimeTooltipModule,
    TextFieldModule,
    CodeAndLabelSelectComponent,
    MissionAcceptanceFormRowComponent,
    DigitOnlyDirective,
    MissionAcceptanceFilesFormTableComponent,
    MapperPipe
  ],
  templateUrl: './informations-form.component.html',
  styleUrl: './informations-form.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class InformationsFormComponent implements OnInit {
  readonly types: InputSignal<CodeAndLabel<MissionAcceptanceType>[]> =
    input.required<CodeAndLabel<MissionAcceptanceType>[]>();
  readonly fileTypes: InputSignal<CodeAndLabel<MissionAcceptanceFileType>[]> =
    input.required<CodeAndLabel<MissionAcceptanceFileType>[]>();
  readonly users: InputSignal<User[]> = input.required<User[]>();
  readonly canAddContractLdmFromContract: InputSignal<boolean> = input.required<boolean>();
  readonly identityPieceIsNeededAndMissing: InputSignal<boolean> = input.required<boolean>();

  readonly removeFile: OutputEmitterRef<string> = output<string>();
  readonly downloadFile: OutputEmitterRef<number> = output<number>();
  readonly openFileInNewTab: OutputEmitterRef<number> = output<number>();
  readonly onFileSelected: OutputEmitterRef<MissionAcceptanceFileSelectedOutput> =
    output<MissionAcceptanceFileSelectedOutput>();
  readonly addContractFile: OutputEmitterRef<void> = output<void>();

  readonly fileTypesWithoutContractLdm: Signal<CodeAndLabel<MissionAcceptanceFileType>[]> = computed(
    () =>
      this.fileTypes()?.filter(
        (fileType: CodeAndLabel<MissionAcceptanceFileType>) => fileType.code !== MissionAcceptanceFileType.contractLdm
      ) ?? []
  );

  form: MissionAcceptanceInformationsForm;
  filesForm: FormArray<MissionAcceptanceFileForm>;

  readonly MissionAcceptanceFileType = MissionAcceptanceFileType;
  readonly MissionAcceptanceType = MissionAcceptanceType;
  readonly UPLOAD_FILE_CONFIG = UPLOAD_FILE_CONFIG;
  readonly acceptedTypes = UPLOAD_FILE_CONFIG.acceptedTypes;
  readonly fileFormsWithoutContractLdmColumns = ['name', 'title', 'createdAtBy', 'actions'];
  readonly addContractLdmContext: AddFileContext = {
    type: MissionAcceptanceFileType.contractLdm,
    text: 'Ajouter une lettre de mission signée'
  };
  readonly addIdentityPieceContext: AddFileContext = {
    type: MissionAcceptanceFileType.identityPiece,
    text: `Ajouter la pièce d'identité du dirigeant`
  };
  readonly addOtherContext: AddFileContext = {
    type: MissionAcceptanceFileType.other,
    text: 'Ajouter une pièce jointe'
  };

  private readonly subscription: Subscription = new Subscription();

  readonly fileFormsWithoutContractLdm: Mapper<MissionAcceptanceFileForm[], MissionAcceptanceFileForm[]> = (
    controls: MissionAcceptanceFileForm[]
  ) => controls?.filter(control => control.type.value !== MissionAcceptanceFileType.contractLdm) ?? [];

  readonly fileFormsWithContractLdm: Mapper<MissionAcceptanceFileForm[], MissionAcceptanceFileForm[]> = (
    controls: MissionAcceptanceFileForm[]
  ) => controls?.filter(control => control.type.value === MissionAcceptanceFileType.contractLdm) ?? [];

  constructor(private readonly formGroupDirective: FormGroupDirective) {}

  ngOnInit(): void {
    this.form = this.formGroupDirective.control.get('informations') as MissionAcceptanceInformationsForm;
    this.filesForm = this.formGroupDirective.control.get('files') as FormArray<MissionAcceptanceFileForm>;

    this.observeTypeChange();
    this.observeFilesChange();
  }

  emitRemoveFile(uniqId: string): void {
    this.removeFile.emit(uniqId);
  }

  emitDownloadFile(fileId: number): void {
    this.downloadFile.emit(fileId);
  }

  emitOpenFileInNewTab(fileId: number): void {
    this.openFileInNewTab.emit(fileId);
  }

  importFile(fileSelector: HTMLInputElement): void {
    fileSelector.value = '';
    fileSelector.click();
  }

  emitOnFileSelected(fileList: FileList, type: MissionAcceptanceFileType): void {
    this.onFileSelected.emit({ fileList, type });
  }

  emitAddContractFile(): void {
    this.addContractFile.emit();
  }

  private observeTypeChange(): void {
    this.subscription.add(
      this.form.controls.type.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap(() => this.form.clearNotDisplayedFieldsByType())
        )
        .subscribe()
    );
  }

  private observeFilesChange(): void {
    this.subscription.add(
      this.filesForm.valueChanges
        .pipe(
          distinctUntilChanged(),
          filter((forms: Partial<MissionAcceptanceFile>[]) =>
            forms.some((form: Partial<MissionAcceptanceFile>) => form.type === MissionAcceptanceFileType.identityPiece)
          ),
          tap(() => this.form.removeMissingIdentityPieceError())
        )
        .subscribe()
    );
  }
}
